import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';

export default function LcpCertificateFastAnimation() {
  const [greenProgress, setGreenProgress] = useState(0);
  
  useEffect(() => {
    const duration = 2000; // 2 seconds
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      setGreenProgress(progress);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  }, []);
  
  const size = 200;
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  
  // ============================================
  // 🎨 CUSTOMIZATION SECTION - MODIFY HERE
  // ============================================
  
  // 🟠 ORANGE SEGMENT LENGTH (0-100%)
  const ORANGE_SEGMENT_PERCENT = 25;
  
  // 🟢 GREEN SEGMENT LENGTH (0-100%)
  const GREEN_SEGMENT_PERCENT = 45;
  
  // 📏 GAP BETWEEN SEGMENTS (in degrees, 0 = no gap)
  const GAP_DEGREES = 5;
  
  // ============================================
  // END CUSTOMIZATION SECTION
  // ============================================
  
  // Calculations
  const orangePercent = ORANGE_SEGMENT_PERCENT;
  const orangeLength = (orangePercent / 100) * circumference;
  
  const greenPercent = GREEN_SEGMENT_PERCENT * greenProgress;
  const greenLength = (greenPercent / 100) * circumference;
  
  const gap = 0;
  
  // Center point
  const center = size / 2;
  
  // Calculate angles for indicator positions (starting from top, going clockwise)
  const orangeStartAngle = 0; // Start at top
  const orangeEndAngle = (orangePercent / 100) * 360; // End of orange segment
  const greenStartAngle = orangeEndAngle + GAP_DEGREES; // Start after gap
  const greenEndAngle = greenStartAngle + (greenPercent / 100) * 360; // End of green segment
  
  // Convert to radians for calculation (subtract 90 because SVG starts at 3 o'clock, we want 12 o'clock)
  const orangeStartRad = ((orangeStartAngle - 90) * Math.PI) / 180;
  const orangeEndRad = ((orangeEndAngle - 90) * Math.PI) / 180;
  const greenStartRad = ((greenStartAngle - 90) * Math.PI) / 180;
  const greenEndRad = ((greenEndAngle - 90) * Math.PI) / 180;
  
  // Calculate positions as percentages for all circle indicators
  const orangeStartX = 50 + (radius / center) * 50 * Math.cos(orangeStartRad);
  const orangeStartY = 50 + (radius / center) * 50 * Math.sin(orangeStartRad);
  
  const orangeEndX = 50 + (radius / center) * 50 * Math.cos(orangeEndRad);
  const orangeEndY = 50 + (radius / center) * 50 * Math.sin(orangeEndRad);
  
  const greenStartX = 50 + (radius / center) * 50 * Math.cos(greenStartRad);
  const greenStartY = 50 + (radius / center) * 50 * Math.sin(greenStartRad);
  
  const greenEndX = 50 + (radius / center) * 50 * Math.cos(greenEndRad);
  const greenEndY = 50 + (radius / center) * 50 * Math.sin(greenEndRad);
  
  return (
    <div className="flex flex-col items-center justify-center pb-16">
      <div className='w-full max-w-md flex justify-between items-center'>
        <div className='px-2 py-1 text-center font-semibold text-white text-[10px] md:text-[12px] rounded-[4px]' style={{backgroundImage : `linear-gradient(90deg, #2BBD13 0%, #192E42 100%)` , boxShadow : '0px 0px 10px 0px rgba(5, 14, 21, 0.4)'}}>
        3x FASTER
        <br/>
        CAREER GROWTH
        </div>

        <div className='px-2 py-1 text-center font-semibold text-white text-[10px] md:text-[12px] rounded-[4px] opacity-50' style={{backgroundImage : `linear-gradient(90deg, #EF9121 0%, #192E42 100%)` , boxShadow : '0px 0px 10px 0px rgba(5, 14, 21, 0.4)'}}>
        3x FASTER
        <br/>
        CAREER GROWTH
        </div>
      </div>
      
      <div className='w-full mt-4 px-[15%] md:px-[20%] flex justify-center items-center'>
        <div className="relative w-full max-w-xs md:max-w-md aspect-square">
            <div className='absolute z-[100] top-[15%] -left-[25%] w-[60%]'>
              <img alt="Certificate" src="https://storage.googleapis.com/learntube-web-assets/files/OnBoardingPage/%20animation/Group%2023595.png" className='w-full h-full' />
            </div>
            <svg width="100%" height="100%" viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90">
            <defs>
                {/* Orange gradient */}
                <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#F5D77E" />
                <stop offset="100%" stopColor="#F59E42" />
                </linearGradient>
                
                {/* Green gradient */}
                <linearGradient id="greenGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#86E889" />
                <stop offset="100%" stopColor="#22C55E" />
                </linearGradient>
            </defs>
            
            {/* Background circle */}
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="#406AFF"
                opacity={0.2}
                strokeWidth={strokeWidth * 0.6}
            />
            
            {/* Orange gradient segment */}
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="url(#orangeGradient)"
                strokeWidth={strokeWidth}
                strokeDasharray={`${orangeLength} ${circumference}`}
                strokeDashoffset={0}
                strokeLinecap="round"
            />
            
            {/* Green gradient segment */}
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="url(#greenGradient)"
                strokeWidth={strokeWidth}
                strokeDasharray={`${greenLength} ${circumference}`}
                strokeDashoffset={-(orangeLength + gap)}
                strokeLinecap="round"
            />
            </svg>
            
            {/* Center content div */}
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="px-4 flex flex-col items-center justify-center text-center gap-2">
                    {/* Add your content here */}
                    <div className="text-[#70AEFF] font-medium text-[12px] md:text-[14px]">
                    Take A Quick <br/> Test   
                    </div>
                    <FontAwesomeIcon icon={faArrowDown} className='text-white text-2xl' />
                    <div className="text-[#70AEFF] font-medium text-[12px] md:text-[14px]">
                    Get Certified <br/> Instantly  
                    </div>
                </div>
            </div>
            
            
            {/* Circle indicator at orange end */}
            <div 
            className="absolute w-3 h-3 bg-white rounded-full"
            style={{
                left: `${orangeEndX}%`,
                top: `${orangeEndY}%`,
                transform: 'translate(-50%, -50%)',
                boxShadow : '0px 0px 20px 0px rgba(246, 184, 53, 1)'
            }}
            />
            
            {/* Circle indicator at green end */}
            <div 
            className="absolute w-[18px] h-[18px] bg-white rounded-full"
            style={{
                left: `${greenEndX}%`,
                top: `${greenEndY}%`,
                transform: 'translate(-50%, -50%)',
                boxShadow : '0px 0px 20px 0px rgba(64, 106, 255, 1)'
            }}
            />

            <div className='absolute z-[100] -bottom-[15%] -right-[22%]  w-[45%]'>
              <img alt="" src="https://storage.googleapis.com/learntube-web-assets/files/OnBoardingPage/%20animation/Group%2023598%20(1).png" className='w-full h-full object-cover' />
            </div>
            <div className='absolute  z-[100] -bottom-[15%] -left-[20%] w-[45%]'>
              <img alt="" src="https://storage.googleapis.com/learntube-web-assets/files/OnBoardingPage/%20animation/Group%2023598.png" className='w-full h-full object-cover' />
            </div>

            <div className='absolute z-[100] bottom-[10%] -right-[20%] w-[45%]'>
              <img alt="" src="https://storage.googleapis.com/learntube-web-assets/files/OnBoardingPage/%20animation/Group%2023341.png" className='w-full h-full object-cover' />
            </div>
        </div>
      </div>
    </div>
  );
}